import {
	faBullseye as fasBullseye,
	faMoneyBill1 as fasMoneyBill1,
	faHandHoldingDollar as fasHandHoldingDollar,
	faFloppyDisk as fasFloppyDisk,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { http, PromisePool, focusOnFirstElement, upperFirst } from "bm-ui-kit";
import type { FormApi } from "final-form";
import createDecorator from "final-form-focus";
import React from "react";
import type { ReactNode } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import { Alert } from "../../Alert";
import { Button } from "../../Button";
import { FormCurrency } from "../../FormCurrency";
import { FormDate } from "../../FormDate";
import type { FormSelectOptions, FormSelectOptionsFn } from "../../FormSelect";
import { FormSelect } from "../../FormSelect";
import type { FormIncomeSimpleData } from "./FormIncomeItem";

const focusOnError = createDecorator();

const MIN_AMOUNT = 5_000;
const MAX_AMOUNT = 5_000_000;

interface Props {
	apiAddress: string;
	show: boolean;
	initialValues?: FormIncomeSimpleData;
	withReceiptAmount?: boolean;
	withIncomeStart?: boolean;
	onSave(income: FormIncomeSimpleData): void;
	onHide(): void;
}

interface State {
	incomeTypes: FormSelectOptions;
	alertClosed: boolean;
}

export class SimpleIncomeModal extends React.Component<Props, State> {
	private readonly promisePool = new PromisePool();

	public constructor(props: Props) {
		super(props);

		this.state = {
			incomeTypes: [],
			alertClosed: false,
		};
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	@autobind
	private loadIncomeTypes(): FormSelectOptionsFn<string> {
		const { apiAddress } = this.props;

		return {
			id: apiAddress,
			fn: () =>
				this.promisePool
					.add(http.getWithCache(apiAddress))
					.then((response) => {
						const incomeTypes = Object.keys(response).map((value) => ({
							label: upperFirst(response[value]),
							value,
						}));

						this.setState({
							incomeTypes,
						});

						return incomeTypes;
					})
					.catch((e) => {
						this.setState({
							incomeTypes: [],
						});

						throw e;
					}),
		};
	}

	public renderChildren(): ReactNode {
		const { withReceiptAmount, withIncomeStart } = this.props;

		return (
			<Row>
				<Col xs={12}>
					<FormSelect
						label="Bevétel típus (havi)"
						name="incomeType"
						options={this.loadIncomeTypes()}
						prepend={<FontAwesomeIcon icon={fasBullseye} />}
						placeholder="Válassz ki egy jövedelem típus!"
						required
					/>
				</Col>
				<Col sm={withReceiptAmount ? 6 : 12}>
					<FormCurrency
						label="Bevétel összege"
						name="amount"
						prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
						validate={this.validAmount}
						required
					/>
				</Col>
				{withReceiptAmount && (
					<Col sm={6}>
						<FormCurrency
							label="Ebből számládra érkezik"
							name="receiptAmount"
							prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
							validate={this.validReceiptAmount}
							required
						/>
					</Col>
				)}
				{withIncomeStart && (
					<Col sm={6}>
						<FormDate
							label="Munkaviszony kezdete"
							name="incomeStart"
							prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
							validate={this.validIncomeStart}
							required
						/>
					</Col>
				)}
			</Row>
		);
	}

	public render(): ReactNode {
		const { show, initialValues } = this.props;
		const { alertClosed } = this.state;

		return (
			<FinalForm<FormIncomeSimpleData>
				initialValues={initialValues}
				decorators={[focusOnError]}
				onSubmit={this.handleSubmit}
			>
				{({ form }) => (
					<Modal centered show={show} onEnter={focusOnFirstElement} onHide={() => this.handleHide(form)}>
						<Modal.Header closeButton>
							<Modal.Title className="mb-0">
								<FontAwesomeIcon icon={fasHandHoldingDollar} className="mx-2" /> Bevételek
							</Modal.Title>
						</Modal.Header>
						<Modal.Body className="p-3">
							<Alert
								icon="auto"
								size="sm"
								variant="danger"
								show={!alertClosed}
								onClose={this.handleAlertClose}
								dismissible
							>
								Csak az igazolható nettó bevételeket adja meg!
							</Alert>
							{this.renderChildren()}
						</Modal.Body>
						<Modal.Footer>
							<Button
								className={form.getState().valid ? "" : "disabled"}
								icon={<FontAwesomeIcon icon={fasFloppyDisk} />}
								onClick={form.submit}
							>
								Mentés
							</Button>
						</Modal.Footer>
					</Modal>
				)}
			</FinalForm>
		);
	}

	@autobind
	protected handleSubmit(oldValues: FormIncomeSimpleData, formApi: FormApi<FormIncomeSimpleData>): void {
		const { withReceiptAmount, onSave } = this.props;
		const incomeData = { ...oldValues };
		incomeData.incomeTypeName = this.getIncomeName(incomeData.incomeType);

		if (!withReceiptAmount) {
			incomeData.receiptAmount = incomeData.amount;
		}

		this.promisePool.addTimeout(0).then(formApi.reset);
		onSave(incomeData);
	}

	@autobind
	private handleAlertClose(): void {
		this.setState({
			alertClosed: true,
		});
	}

	@autobind
	protected handleHide(formApi: FormApi<FormIncomeSimpleData>): void {
		const { onHide } = this.props;

		this.promisePool.addTimeout(0).then(formApi.reset);
		onHide();
	}

	private getIncomeName(incomeType: string): string {
		const { incomeTypes } = this.state;
		const incomeTypeData = incomeTypes.find((income) => income.value === incomeType);

		return incomeTypeData ? incomeTypeData.label : "Ismeretlen jövedelemtípus";
	}

	@autobind
	protected validAmount(amount: number): string {
		if (amount < MIN_AMOUNT) {
			return `A minimálisan megadható jövedelem ${formatText(MIN_AMOUNT, "ft")}`;
		}

		if (amount > MAX_AMOUNT) {
			return `A maximálisan megadható jövedelem ${formatText(MAX_AMOUNT, "ft")}`;
		}
	}

	@autobind
	private validIncomeStart(incomeStart: string): string {
		if (!incomeStart) {
			return "A bevétel kezdetének megadása kötelező!";
		}

		const date = new Date(incomeStart);

		if (date.getTime() > new Date().getTime()) {
			return "A bevételszerzés kezdete nem lehet jövőbeli!";
		}
	}

	@autobind
	protected validReceiptAmount(receiptAmount: number): string {
		if (isNaN(receiptAmount)) {
			return "A mező kitöltése kötelező!";
		}

		if (receiptAmount < 0) {
			return `A bankhoz érkeztetett összeg nem lehet kissebb mint nulla!`;
		}

		if (receiptAmount > MAX_AMOUNT) {
			return `A maximálisan megadható bankhoz érkeztetett jövedelem ${formatText(MAX_AMOUNT, "ft")}`;
		}
	}
}
